import React, { useEffect, useState, useRef, Component } from 'react';
import { createPortal } from 'react-dom';
import { GlobeIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon, BarChart2Icon, LinkIcon, XIcon, LockIcon, ServerIcon, DatabaseIcon, CreditCardIcon, UsersIcon, FileTextIcon, WalletIcon, ReceiptIcon, BanknoteIcon, SettingsIcon, CheckIcon, CopyIcon, PlusIcon } from 'lucide-react';
interface OAuth2App {
  id: string;
  name: string;
  logo: string;
  publicKey: string;
  privateKey: string;
  scopes: string[];
  status: 'active' | 'inactive';
  transactionVolume: number;
  transactionCount: number;
  domains: string[];
  connectedDate: string;
  lastUsed: string;
}
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
const scopeConfig: Record<string, {
  name: string;
  icon: React.ReactNode;
}> = {
  transactions: {
    name: 'Transactions',
    icon: <CreditCardIcon size={12} className="text-[#F7941D]" data-id="element-7172" />
  },
  refunds: {
    name: 'Refunds',
    icon: <RefreshCwIcon size={12} className="text-orange-500" data-id="element-7173" />
  },
  company_info: {
    name: 'Company Info',
    icon: <UsersIcon size={12} className="text-green-600" data-id="element-7174" />
  },
  customer_data: {
    name: 'Customer Data',
    icon: <DatabaseIcon size={12} className="text-purple-600" data-id="element-7175" />
  },
  suppliers: {
    name: 'Suppliers',
    icon: <ServerIcon size={12} className="text-blue-500" data-id="element-7176" />
  },
  bills: {
    name: 'Bills',
    icon: <FileTextIcon size={12} className="text-rose-500" data-id="element-7177" />
  },
  invoices: {
    name: 'Invoices',
    icon: <ReceiptIcon size={12} className="text-teal-600" data-id="element-7178" />
  },
  balance: {
    name: 'Balance',
    icon: <WalletIcon size={12} className="text-emerald-600" data-id="element-7179" />
  },
  settlements: {
    name: 'Settlements',
    icon: <BanknoteIcon size={12} className="text-indigo-500" data-id="element-7180" />
  },
  analytics: {
    name: 'Analytics',
    icon: <BarChart2Icon size={12} className="text-amber-500" data-id="element-7181" />
  },
  settings: {
    name: 'Settings',
    icon: <SettingsIcon size={12} className="text-gray-500" data-id="element-7182" />
  }
};
const defaultApps: OAuth2App[] = [{
  id: 'oauth-001',
  name: 'Shopify',
  logo: 'https://cdn.shopify.com/s/files/1/0533/2089/files/Shopify_logo_small.png?v=1512152678',
  publicKey: 'pub_key_6a8b4c2d3e7f9g0h1i2j',
  privateKey: 'prv_key_5f4e3d2c1b0a9z8y7x6w5v4u3t2s1r',
  scopes: ['transactions', 'refunds', 'company_info'],
  status: 'active',
  transactionVolume: 87500,
  transactionCount: 1243,
  domains: ['mystore.shopify.com', 'checkout.shopify.com'],
  connectedDate: '2023-05-12',
  lastUsed: '2 hours ago'
}, {
  id: 'oauth-002',
  name: 'Opera PMS',
  logo: 'https://www.oracle.com/a/ocom/img/cb71-opera-cloud-services.jpg',
  publicKey: 'pub_key_7b9c5d3e1f8g2h4i6j0k',
  privateKey: 'prv_key_6g5f4e3d2c1b0a9z8y7x6w5v4u3t',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 125400,
  transactionCount: 2156,
  domains: ['operahotel.com', 'api.operacloud.com'],
  connectedDate: '2023-02-28',
  lastUsed: '5 minutes ago'
}, {
  id: 'oauth-003',
  name: 'CloudBeds',
  logo: 'https://assets.cloudbeds.com/static/images/cloudbeds-logo.svg',
  publicKey: 'pub_key_8c0d6e4f2g9h1i3j5k7l',
  privateKey: 'prv_key_7h6g5f4e3d2c1b0a9z8y7x6w5v',
  scopes: ['transactions', 'refunds', 'customer_data'],
  status: 'active',
  transactionVolume: 65800,
  transactionCount: 954,
  domains: ['app.cloudbeds.com', 'api.cloudbeds.com'],
  connectedDate: '2023-08-15',
  lastUsed: '1 day ago'
}, {
  id: 'oauth-004',
  name: 'Mews Systems',
  logo: 'https://mews-careers.cdn.prismic.io/mews-careers/a84d4ff0-6b90-44a2-9f7e-c5b511ef17d2_mews-logo.svg',
  publicKey: 'pub_key_9d1e7f5g3h0i2j4k6l8m',
  privateKey: 'prv_key_8i7h6g5f4e3d2c1b0a9z8y7x6w',
  scopes: ['transactions', 'company_info', 'settlements'],
  status: 'inactive',
  transactionVolume: 32400,
  transactionCount: 567,
  domains: ['mews.com', 'api.mews.com'],
  connectedDate: '2023-04-10',
  lastUsed: '3 weeks ago'
}, {
  id: 'oauth-006',
  name: 'Salesforce',
  logo: 'https://www.salesforce.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg',
  publicKey: 'pub_key_1f3g5h7i9j0k2l4m6n8o',
  privateKey: 'prv_key_0k9j8i7h6g5f4e3d2c1b0a9z8y',
  scopes: ['company_info', 'customer_data'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['salesforce.com', 'force.com'],
  connectedDate: '2023-09-05',
  lastUsed: '3 days ago'
}, {
  id: 'oauth-008',
  name: 'Xero Accounting',
  logo: 'https://www.xero.com/content/dam/xero/pilot-images/brand/logos/logo-xero-blue.svg',
  publicKey: 'pub_key_3h5i7j9k1l2m4n6o8p0q',
  privateKey: 'prv_key_2m1l0k9j8i7h6g5f4e3d2c1b0a',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['xero.com', 'api.xero.com'],
  connectedDate: '2023-07-22',
  lastUsed: '12 hours ago'
}];
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-7183">
      <button type="button" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D]" data-id="element-7184">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-7185" />}
        <span className="truncate" data-id="element-7186">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-7187" />
      </button>
      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-7188">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#F7941D]/5 font-medium' : 'text-gray-700'}`} data-id="element-7189">
              <div className="flex items-center gap-2" data-id="element-7190">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-7191" />}
                <span data-id="element-7192">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#F7941D] flex-shrink-0" data-id="element-7193" />}
            </button>)}
        </div>}
    </div>;
}
const getStatusBadge = (status: 'active' | 'inactive') => {
  const styles = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-red-50 text-red-700'
  };
  const icons = {
    active: <CheckCircleIcon size={11} className="mr-1" data-id="element-7194" />,
    inactive: <XCircleIcon size={11} className="mr-1" data-id="element-7195" />
  };
  const labels = {
    active: 'Active',
    inactive: 'Inactive'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-7196">
      {icons[status]}
      {labels[status]}
    </span>;
};
function generateKey(prefix: string) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let key = `${prefix}_`;
  for (let i = 0; i < 24; i++) key += chars[Math.floor(Math.random() * chars.length)];
  return key;
}
export function Connect() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPrivateKeys, setShowPrivateKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [apps, setApps] = useState<OAuth2App[]>(defaultApps);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [editingApp, setEditingApp] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomains, setEditDomains] = useState('');
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const togglePrivateKey = (appId: string) => {
    setShowPrivateKeys(prev => ({
      ...prev,
      [appId]: !prev[appId]
    }));
  };
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 1500);
  };
  const requestToggle = (appId: string) => {
    setPendingToggle(appId);
  };
  const confirmToggle = () => {
    if (!pendingToggle) return;
    setApps(prev => prev.map(app => app.id === pendingToggle ? {
      ...app,
      status: app.status === 'active' ? 'inactive' : 'active'
    } : app));
    setPendingToggle(null);
  };
  const pendingApp = pendingToggle ? apps.find(a => a.id === pendingToggle) : null;
  const openEditModal = (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;
    setEditingApp(appId);
    setEditName(app.name);
    setEditDomains(app.domains.join(', '));
    setEditScopes([...app.scopes]);
  };
  const toggleEditScope = (scopeId: string) => {
    setEditScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleSaveEdit = () => {
    if (!editingApp || !editName.trim()) return;
    const domains = editDomains.split(',').map(d => d.trim()).filter(Boolean);
    setApps(prev => prev.map(app => app.id === editingApp ? {
      ...app,
      name: editName.trim(),
      domains: domains.length > 0 ? domains : app.domains,
      scopes: editScopes.length > 0 ? editScopes : app.scopes
    } : app));
    setEditingApp(null);
  };
  const toggleScope = (scopeId: string) => {
    setNewScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleAddApp = () => {
    if (!newName.trim()) return;
    const domains = newDomains.split(',').map(d => d.trim()).filter(Boolean);
    const newApp: OAuth2App = {
      id: `oauth-${Date.now()}`,
      name: newName.trim(),
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName.trim())}&background=F7941D&color=fff&size=32`,
      publicKey: generateKey('pub_key'),
      privateKey: generateKey('prv_key'),
      scopes: newScopes.length > 0 ? newScopes : ['transactions'],
      status: 'active',
      transactionVolume: 0,
      transactionCount: 0,
      domains: domains.length > 0 ? domains : [`${newName.trim().toLowerCase().replace(/\s+/g, '')}.com`],
      connectedDate: new Date().toISOString().split('T')[0],
      lastUsed: 'Just now'
    };
    setApps(prev => [newApp, ...prev]);
    setNewName('');
    setNewDomains('');
    setNewScopes([]);
    setShowAddModal(false);
  };
  const filteredApps = apps.filter(app => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = app.name.toLowerCase().includes(q) || app.publicKey.includes(searchQuery) || app.domains.some(d => d.toLowerCase().includes(q));
    return matchesSearch && (statusFilter === 'all' || app.status === statusFilter);
  });
  const totalConnections = apps.length;
  const activeConnections = apps.filter(a => a.status === 'active').length;
  const totalVolume = apps.reduce((s, a) => s + a.transactionVolume, 0);
  return <div className="space-y-5" data-id="element-7197">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-7198">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-7199">
          <div className="h-10 w-10 rounded-lg bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0" data-id="element-7200">
            <GlobeIcon size={18} className="text-[#F7941D]" data-id="element-7201" />
          </div>
          <div data-id="element-7202">
            <p className="text-xs text-gray-500" data-id="element-7203">Total connections</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-7204">
              {totalConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-7205">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-7206">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-7207" />
          </div>
          <div data-id="element-7208">
            <p className="text-xs text-gray-500" data-id="element-7209">Active connections</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-7210">
              {activeConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-7211">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-7212">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-7213" />
          </div>
          <div data-id="element-7214">
            <p className="text-xs text-gray-500" data-id="element-7215">Total volume</p>
            <p className="text-xl font-bold text-gray-900" data-id="element-7216">
              THB {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7217">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-7218">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7219">
            <GlobeIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-7220" />
            OAuth2 connections
          </h2>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5" data-id="element-7221">
            <PlusIcon size={14} data-id="element-7222" />
            Add connection
          </button>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-7223">
          <div className="relative flex-1" data-id="element-7224">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-7225" />
            <input type="text" className={`${inputClass} pl-9`} placeholder="Search by app name, API key, or domain..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7226" />
          </div>
          <div className="flex items-center gap-2" data-id="element-7227">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'active',
            label: 'Active',
            dot: 'bg-green-500'
          }, {
            value: 'inactive',
            label: 'Inactive',
            dot: 'bg-red-500'
          }]} data-id="element-7228" />
            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-7229">
              <RefreshCwIcon size={13} data-id="element-7230" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ─── APP GRID ─── */}
      {filteredApps.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-7231">
          {filteredApps.map(app => <div key={app.id} className="bg-white rounded-xl border border-gray-200" data-id="element-7232">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-7233">
                <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-7234">
                  <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-7235">
                    <img src={app.logo} alt={app.name} className="w-full h-full object-contain p-1" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.name}&background=F7941D&color=fff&size=32`;
              }} data-id="element-7236" />
                  </div>
                  <div className="min-w-0" data-id="element-7237">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-7238">
                      {app.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-7239">
                      Connected {app.connectedDate}
                    </p>
                  </div>
                </div>
                <button onClick={() => requestToggle(app.id)} className="relative flex-shrink-0 ml-3" title={app.status === 'active' ? 'Deactivate' : 'Activate'} data-id="element-7240">
                  <div className={`w-9 h-5 rounded-full transition-colors ${app.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-7241">
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${app.status === 'active' ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-7242" />
                  </div>
                </button>
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-7243">
                {/* Public key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-7244">
                  <div className="flex items-center justify-between mb-1" data-id="element-7245">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-7246">
                      <KeyIcon size={11} className="text-gray-400" data-id="element-7247" />
                      Public key
                    </span>
                    <button onClick={() => copyToClipboard(app.publicKey, `${app.id}-pub`)} className="text-gray-400 hover:text-[#F7941D] transition-colors" data-id="element-7248">
                      {copiedKey === `${app.id}-pub` ? <CheckIcon size={12} className="text-green-500" data-id="element-7249" /> : <CopyIcon size={12} data-id="element-7250" />}
                    </button>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-7251">
                    {app.publicKey}
                  </code>
                </div>

                {/* Private key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-7252">
                  <div className="flex items-center justify-between mb-1" data-id="element-7253">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-7254">
                      <LockIcon size={11} className="text-gray-400" data-id="element-7255" />
                      Private key
                    </span>
                    <div className="flex items-center gap-1.5" data-id="element-7256">
                      <button onClick={() => togglePrivateKey(app.id)} className="text-gray-400 hover:text-[#F7941D] transition-colors" data-id="element-7257">
                        {showPrivateKeys[app.id] ? <EyeOffIcon size={12} data-id="element-7258" /> : <EyeIcon size={12} data-id="element-7259" />}
                      </button>
                      <button onClick={() => copyToClipboard(app.privateKey, `${app.id}-prv`)} className="text-gray-400 hover:text-[#F7941D] transition-colors" data-id="element-7260">
                        {copiedKey === `${app.id}-prv` ? <CheckIcon size={12} className="text-green-500" data-id="element-7261" /> : <CopyIcon size={12} data-id="element-7262" />}
                      </button>
                    </div>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-7263">
                    {showPrivateKeys[app.id] ? app.privateKey : '••••••••••••••••••••••••••••••'}
                  </code>
                </div>

                {/* Scopes */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-7264">
                  <span className="text-xs text-gray-500 block mb-1.5" data-id="element-7265">
                    Scopes
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-7266">
                    {app.scopes.map(s => {
                const cfg = scopeConfig[s];
                if (!cfg) return null;
                return <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[11px] text-gray-700" data-id="element-7267">
                          {cfg.icon}
                          {cfg.name}
                        </span>;
              })}
                  </div>
                </div>

                {/* Domains */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-7268">
                  <span className="text-xs text-gray-500 flex items-center gap-1 mb-1.5" data-id="element-7269">
                    <LinkIcon size={11} className="text-gray-400" data-id="element-7270" />
                    Domains
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-7271">
                    {app.domains.map(d => <span key={d} className="inline-flex items-center px-2 py-0.5 bg-[#F7941D]/5 text-[#F7941D] text-[11px] font-medium rounded" data-id="element-7272">
                        {d}
                      </span>)}
                  </div>
                </div>

                {/* Volume & count */}
                <div className="grid grid-cols-2" data-id="element-7273">
                  <div className="px-5 py-2.5 border-b border-gray-100 border-r border-gray-100" data-id="element-7274">
                    <span className="text-xs text-gray-500" data-id="element-7275">Volume</span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-7276">
                      THB {app.transactionVolume.toLocaleString()}
                    </p>
                  </div>
                  <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-7277">
                    <span className="text-xs text-gray-500" data-id="element-7278">Transactions</span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-7279">
                      {app.transactionCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 flex items-center justify-between" data-id="element-7280">
                <span className="text-[11px] text-gray-400" data-id="element-7281">
                  Last used {app.lastUsed}
                </span>
                <button onClick={() => openEditModal(app.id)} className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors flex items-center gap-1" data-id="element-7282">
                  <SettingsIcon size={11} data-id="element-7283" />
                  Settings
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-7284">
          <GlobeIcon size={24} className="text-gray-400 mb-3" data-id="element-7285" />
          <p className="text-sm font-medium text-gray-700" data-id="element-7286">
            No connections found
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-7287">
            No connections match your search criteria. Try adjusting your
            filters.
          </p>
        </div>}

      {/* ─── ADD CONNECTION MODAL ─── */}
      {showAddModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowAddModal(false);
    }} data-id="element-7288">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-7289">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-7290">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-7291">
                  Add connection
                </h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7292">
                  <XIcon size={18} data-id="element-7293" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-7294">
                {/* App name */}
                <div data-id="element-7295">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7296">
                    App name <span className="text-red-400" data-id="element-7297">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder="e.g., Opera PMS, Salesforce" value={newName} onChange={e => setNewName(e.target.value)} autoFocus data-id="element-7298" />
                </div>

                {/* Domains */}
                <div data-id="element-7299">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7300">
                    Authorized domains
                  </label>
                  <input type="text" className={inputClass} placeholder="example.com, api.example.com" value={newDomains} onChange={e => setNewDomains(e.target.value)} data-id="element-7301" />
                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-7302">
                    Comma-separated list of domains
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-7303">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7304">
                    Scopes
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-7305">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${newScopes.includes(id) ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7306">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${newScopes.includes(id) ? 'bg-[#F7941D] border-[#F7941D]' : 'border-gray-300'}`} data-id="element-7307">
                          {newScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-7308" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-7309">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-7310">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-7311">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-7312">
                  Cancel
                </button>
                <button onClick={handleAddApp} disabled={!newName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${newName.trim() ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-7313">
                  Create connection
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── TOGGLE CONFIRMATION MODAL ─── */}
      {pendingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setPendingToggle(null);
    }} data-id="element-7314">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-7315">
              <div className="p-6 text-center" data-id="element-7316">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${pendingApp.status === 'active' ? 'bg-red-50' : 'bg-green-50'}`} data-id="element-7317">
                  {pendingApp.status === 'active' ? <XCircleIcon size={24} className="text-red-500" data-id="element-7318" /> : <CheckCircleIcon size={24} className="text-green-500" data-id="element-7319" />}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-7320">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}{' '}
                  {pendingApp.name}?
                </h3>
                <p className="text-sm text-gray-500" data-id="element-7321">
                  {pendingApp.status === 'active' ? 'This will revoke API access for this connection. All active sessions will be terminated.' : 'This will restore API access for this connection and allow new transactions.'}
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-7322">
                <button onClick={() => setPendingToggle(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-7323">
                  Cancel
                </button>
                <button onClick={confirmToggle} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${pendingApp.status === 'active' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`} data-id="element-7324">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── EDIT CONNECTION MODAL ─── */}
      {editingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setEditingApp(null);
    }} data-id="element-7325">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-7326">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-7327">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-7328">
                  Edit connection
                </h3>
                <button onClick={() => setEditingApp(null)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7329">
                  <XIcon size={18} data-id="element-7330" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-7331">
                {/* App name */}
                <div data-id="element-7332">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7333">
                    App name <span className="text-red-400" data-id="element-7334">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder="e.g., Opera PMS, Salesforce" value={editName} onChange={e => setEditName(e.target.value)} autoFocus data-id="element-7335" />
                </div>

                {/* Domains */}
                <div data-id="element-7336">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7337">
                    Authorized domains
                  </label>
                  <input type="text" className={inputClass} placeholder="example.com, api.example.com" value={editDomains} onChange={e => setEditDomains(e.target.value)} data-id="element-7338" />
                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-7339">
                    Comma-separated list of domains
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-7340">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7341">
                    Scopes
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-7342">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleEditScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${editScopes.includes(id) ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7343">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${editScopes.includes(id) ? 'bg-[#F7941D] border-[#F7941D]' : 'border-gray-300'}`} data-id="element-7344">
                          {editScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-7345" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-7346">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-7347">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0" data-id="element-7348">
                <button onClick={() => setEditingApp(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-7349">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} disabled={!editName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${editName.trim() ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-7350">
                  Save changes
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}